/**
 * Novissimi Theme — Main JavaScript
 * Handles: Dark/Light toggle, mobile nav, cart AJAX, variant selection, gallery
 */

(function() {
  'use strict';

  /* --------------------------------------------------------------------------
     Theme Toggle (Dark / Light)
     -------------------------------------------------------------------------- */
  const ThemeToggle = {
    init() {
      this.html = document.documentElement;
      this.toggleBtns = document.querySelectorAll('[data-theme-toggle]');
      this.savedTheme = localStorage.getItem('novissimi-theme') || 'dark';

      // Apply saved theme
      this.html.setAttribute('data-theme', this.savedTheme);
      this.updateIcons();

      // Bind toggle buttons
      this.toggleBtns.forEach(btn => {
        btn.addEventListener('click', () => this.toggle());
      });
    },

    toggle() {
      const current = this.html.getAttribute('data-theme');
      const next = current === 'dark' ? 'light' : 'dark';

      // Add transition class for smooth color change
      this.html.classList.add('theme-transition');
      this.html.setAttribute('data-theme', next);
      localStorage.setItem('novissimi-theme', next);
      this.updateIcons();

      // Remove transition class after animation completes
      setTimeout(() => {
        this.html.classList.remove('theme-transition');
      }, 400);
    },

    updateIcons() {
      const isDark = this.html.getAttribute('data-theme') === 'dark';
      this.toggleBtns.forEach(btn => {
        const icon = btn.querySelector('.material-symbols-outlined');
        if (icon) {
          icon.textContent = isDark ? 'light_mode' : 'dark_mode';
        }
      });
    }
  };

  /* --------------------------------------------------------------------------
     Mobile Navigation Drawer
     -------------------------------------------------------------------------- */
  const MobileNav = {
    init() {
      this.drawer = document.querySelector('[data-mobile-drawer]');
      this.overlay = document.querySelector('[data-drawer-overlay]');
      this.openBtn = document.querySelector('[data-drawer-open]');
      this.closeBtn = document.querySelector('[data-drawer-close]');

      if (!this.drawer) return;

      this.openBtn?.addEventListener('click', () => this.open());
      this.closeBtn?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    },

    open() {
      this.drawer.classList.add('open');
      this.overlay?.classList.add('open');
      document.body.style.overflow = 'hidden';
    },

    close() {
      this.drawer.classList.remove('open');
      this.overlay?.classList.remove('open');
      document.body.style.overflow = '';
    }
  };

  /* --------------------------------------------------------------------------
     Product Variant Selector
     -------------------------------------------------------------------------- */
  const VariantSelector = {
    init() {
      this.form = document.querySelector('[data-product-form]');
      if (!this.form) return;

      this.variantSelect = this.form.querySelector('[data-variant-select]');
      this.priceEl = document.querySelector('[data-product-price]');
      this.comparePriceEl = document.querySelector('[data-compare-price]');
      this.addToCartBtn = this.form.querySelector('[data-add-to-cart]');
      this.variantsJson = document.querySelector('[data-variants-json]');

      if (this.variantsJson) {
        try {
          this.variants = JSON.parse(this.variantsJson.textContent);
        } catch(e) {
          this.variants = [];
        }
      }

      // Bind swatch clicks
      document.querySelectorAll('[data-option-selector]').forEach(selector => {
        selector.querySelectorAll('[data-option-value]').forEach(btn => {
          btn.addEventListener('click', (e) => this.handleOptionClick(e, selector));
        });
      });
    },

    handleOptionClick(e, selector) {
      const btn = e.currentTarget;
      const optionIndex = selector.dataset.optionIndex;
      const value = btn.dataset.optionValue;

      // Update active state
      selector.querySelectorAll('[data-option-value]').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      // Find matching variant
      this.updateVariant();
    },

    updateVariant() {
      if (!this.variants) return;

      // Get selected options
      const selectedOptions = [];
      document.querySelectorAll('[data-option-selector]').forEach(selector => {
        const active = selector.querySelector('.active[data-option-value]');
        if (active) selectedOptions.push(active.dataset.optionValue);
      });

      // Find variant matching all selected options
      const variant = this.variants.find(v => {
        return v.options.every((opt, i) => opt === selectedOptions[i]);
      });

      if (variant) {
        // Update hidden select
        if (this.variantSelect) {
          this.variantSelect.value = variant.id;
        }

        // Update price
        if (this.priceEl) {
          this.priceEl.textContent = this.formatMoney(variant.price);
        }

        // Update compare price
        if (this.comparePriceEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            this.comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
            this.comparePriceEl.style.display = '';
          } else {
            this.comparePriceEl.style.display = 'none';
          }
        }

        // Update add to cart button state
        if (this.addToCartBtn) {
          if (variant.available) {
            this.addToCartBtn.disabled = false;
            this.addToCartBtn.textContent = 'Add to Cart';
          } else {
            this.addToCartBtn.disabled = true;
            this.addToCartBtn.textContent = 'Sold Out';
          }
        }

        // Update main product image if variant has an image
        if (variant.featured_image) {
          const mainImage = document.querySelector('[data-gallery-main] img');
          if (mainImage) {
            mainImage.src = variant.featured_image.src;
            mainImage.alt = variant.featured_image.alt || '';
          }
        }

        // Update URL without reload
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    },

    formatMoney(cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      let formatString = format || window.theme?.moneyFormat || '€{{amount}}';
      formatString = formatString.replace(/&euro;/g, '€');

      // If store is Euro or theme has Euro currency, preserve Euro
      if (window.theme?.currencySymbol === '€' || window.theme?.currency === 'EUR' || formatString.includes('€')) {
        if (formatString.includes('$')) {
          formatString = formatString.replace('$', '€');
        }
      }

      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = formatString.match(placeholderRegex);
      if (!match) {
        return (formatString.includes('€') ? '€' : '$') + (cents / 100).toFixed(2);
      }

      function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
        if (isNaN(number) || number == null) return '0';
        number = (number / 100.0).toFixed(precision);
        const parts = number.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const decimals = parts[1] ? decimal + parts[1] : '';
        return dollars + decimals;
      }

      let value = '';
      switch (match[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_with_space_separator':
          value = formatWithDelimiters(cents, 2, ' ', '.');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ', '.');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.');
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }

      return formatString.replace(placeholderRegex, value);
    }
  };

  // Expose formatMoney globally on window.theme
  if (typeof window !== 'undefined') {
    window.theme = window.theme || {};
    window.theme.formatMoney = VariantSelector.formatMoney;
  }

  /* --------------------------------------------------------------------------
     Image Gallery
     -------------------------------------------------------------------------- */
  const Gallery = {
    init() {
      this.mainImage = document.querySelector('[data-gallery-main] img');
      this.thumbs = document.querySelectorAll('[data-gallery-thumb]');

      if (!this.mainImage || !this.thumbs.length) return;

      this.thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const src = thumb.dataset.galleryThumb;
          const alt = thumb.dataset.galleryAlt || '';

          this.mainImage.src = src;
          this.mainImage.alt = alt;

          // Update active state
          this.thumbs.forEach(t => t.closest('.gallery__thumb')?.classList.remove('active'));
          thumb.closest('.gallery__thumb')?.classList.add('active');
        });
      });
    }
  };

  /* --------------------------------------------------------------------------
     AJAX Cart
     -------------------------------------------------------------------------- */
  const Cart = {
    init() {
      // Quick add-to-cart buttons
      document.querySelectorAll('[data-quick-add]').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          const variantId = btn.dataset.quickAdd;
          if (variantId) {
            await this.addItem(variantId, 1);
          }
        });
      });

      // Main product form
      const productForm = document.querySelector('[data-product-form]');
      if (productForm) {
        productForm.addEventListener('submit', async (e) => {
          e.preventDefault();
          const formData = new FormData(productForm);
          const variantId = formData.get('id');
          const quantity = parseInt(formData.get('quantity') || '1');
          if (variantId) {
            await this.addItem(variantId, quantity);
          }
        });
      }

      // Quantity selectors in cart
      document.querySelectorAll('[data-quantity-change]').forEach(btn => {
        btn.addEventListener('click', () => {
          const input = btn.parentElement.querySelector('[data-quantity-input]');
          if (!input) return;

          let value = parseInt(input.value);
          const action = btn.dataset.quantityChange;

          if (action === 'minus' && value > 1) {
            input.value = value - 1;
          } else if (action === 'plus') {
            input.value = value + 1;
          }

          // Trigger change for form submission
          input.dispatchEvent(new Event('change'));
        });
      });
    },

    async addItem(variantId, quantity = 1) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: parseInt(variantId),
            quantity: quantity
          })
        });

        if (response.ok) {
          await this.updateCartCount();
          this.showNotification('Added to cart');
        } else {
          const data = await response.json();
          this.showNotification(data.description || 'Could not add to cart', 'error');
        }
      } catch (err) {
        console.error('Cart error:', err);
        this.showNotification('Something went wrong', 'error');
      }
    },

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js', {
          headers: { 'Accept': 'application/json' }
        });
        const cart = await response.json();
        const countEls = document.querySelectorAll('[data-cart-count]');
        countEls.forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? '' : 'none';
        });
      } catch (err) {
        console.error('Cart count update error:', err);
      }
    },

    showNotification(message, type = 'success') {
      // Create notification element
      const notification = document.createElement('div');
      notification.style.cssText = `
        position: fixed;
        bottom: 24px;
        right: 24px;
        padding: 16px 24px;
        border-radius: 12px;
        font-family: var(--font-label);
        font-size: 14px;
        letter-spacing: 0.05em;
        z-index: 9999;
        transform: translateY(20px);
        opacity: 0;
        transition: all 0.3s ease;
        background: ${type === 'error' ? 'var(--color-error-container)' : 'var(--color-surface-container-high)'};
        color: ${type === 'error' ? 'var(--color-on-error-container)' : 'var(--color-on-surface)'};
        border: 1px solid ${type === 'error' ? 'var(--color-error)' : 'var(--color-outline-variant)'};
        backdrop-filter: blur(20px);
      `;
      notification.textContent = message;
      document.body.appendChild(notification);

      // Animate in
      requestAnimationFrame(() => {
        notification.style.transform = 'translateY(0)';
        notification.style.opacity = '1';
      });

      // Remove after delay
      setTimeout(() => {
        notification.style.transform = 'translateY(20px)';
        notification.style.opacity = '0';
        setTimeout(() => notification.remove(), 300);
      }, 3000);
    }
  };

  /* --------------------------------------------------------------------------
     Intersection Observer Animations
     -------------------------------------------------------------------------- */
  const ScrollAnimations = {
    init() {
      const animatedElements = document.querySelectorAll('[data-animate]');
      if (!animatedElements.length) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      animatedElements.forEach(el => {
        el.style.opacity = '0';
        observer.observe(el);
      });
    }
  };

  /* --------------------------------------------------------------------------
     Sticky Filter Sidebar
     -------------------------------------------------------------------------- */
  const StickyFilter = {
    init() {
      const sidebar = document.querySelector('[data-sticky-filter]');
      if (!sidebar) return;

      const filterToggle = document.querySelector('[data-filter-toggle]');
      const filterPanel = document.querySelector('[data-filter-panel]');

      if (filterToggle && filterPanel) {
        filterToggle.addEventListener('click', () => {
          filterPanel.classList.toggle('open');
          const isOpen = filterPanel.classList.contains('open');
          filterToggle.setAttribute('aria-expanded', isOpen);
        });
      }
    }
  };

  /* --------------------------------------------------------------------------
     Initialize All Modules
     -------------------------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', () => {
    ThemeToggle.init();
    MobileNav.init();
    VariantSelector.init();
    Gallery.init();
    Cart.init();
    ScrollAnimations.init();
    StickyFilter.init();
  });

})();
